import orderBy from 'lodash.orderby';
import {
  LeagueDetail,
  LeagueEntry,
  MiniLeague
} from '@api-service/mini-leagues';

export const awardTypes = [
  {
    label: 'Most transfers',
    key: 'The Tinkerer',
    chartLabel: 'Transfers made',
    emoji: '🪄'
  },
  {
    label: 'Least transfers',
    key: 'The Casual',
    chartLabel: 'Transfers made',
    emoji: '🏖️'
  },
  {
    label: 'Most benched points',
    key: 'The Pep',
    chartLabel: 'Benched points',
    emoji: '👨🏼‍🦲'
  },
  {
    label: 'Most template team',
    key: 'The Sheep',
    chartLabel: 'EO%',
    emoji: '🐑'
  },
  {
    label: 'Least template team',
    key: 'The Maverick',
    chartLabel: 'EO%',
    emoji: '🏍️'
  },
  {
    label: 'Most predicted points',
    key: 'Data FC',
    chartLabel: 'Predicted points',
    emoji: '🤓'
  },
  {
    label: 'Biggest difference to predicted points',
    key: 'Lucky FC',
    chartLabel: 'Points above predicted',
    emoji: '🍀'
  },
  {
    label: 'Lowest points of gameweek',
    key: 'The Donkey',
    chartLabel: 'GW points',
    emoji: '👎'
  },
  {
    label: 'Most points over last 5 GWs',
    key: 'In Form FC',
    chartLabel: 'Points last 5 GWs',
    emoji: '🔥'
  },
  {
    label: 'Most points from transfers',
    key: 'The Redknapp',
    chartLabel: 'Points from transfers',
    emoji: '🤝'
  },
  {
    label: 'Least points from transfers',
    key: 'Sack The Manager',
    chartLabel: 'Points from transfers',
    emoji: '🗑️'
  }
];

type AwardItem = {
  teamName: string;
  playerName: string;
  value: number;
};

type AwardKey = (typeof awardTypes)[number]['key'];

/**
 * Orders the entries holding a value for an award, best first. Entries with no
 * value are left out rather than ranked as zero: a league served by the
 * ranking engine does not carry every column yet (FFH-3197, FFH-3198), and a
 * winner picked on nothing is wrong.
 */
const rankBy = (
  teams: LeagueEntry[],
  getValue: (entry: LeagueEntry) => number | null,
  order: 'asc' | 'desc'
): AwardItem[] => {
  const scored = teams.flatMap((entry) => {
    const value = getValue(entry);
    return value === null || value === undefined
      ? []
      : [{ teamName: entry.teamName, playerName: entry.playerName, value }];
  });
  return orderBy(scored, (item) => item.value, order);
};

const differenceOrNull = (left: number, right: number | null) =>
  right === null || right === undefined ? null : left - right;

/**
 * The award standings for a league, keyed by award. An award no entry holds a
 * value for maps to an empty list, which the page leaves out.
 */
export const getAwardsData = (
  teams: LeagueEntry[] | undefined
): Record<AwardKey, AwardItem[]> | undefined => {
  if (!teams) return undefined;

  return {
    'The Tinkerer': rankBy(teams, (entry) => entry.transfers.count, 'desc'),
    'The Casual': rankBy(teams, (entry) => entry.transfers.count, 'asc'),
    'The Pep': rankBy(teams, (entry) => entry.points.benched, 'desc'),
    'The Sheep': rankBy(
      teams,
      (entry) => entry.averageEffectiveOwnership,
      'desc'
    ),
    'The Maverick': rankBy(
      teams,
      (entry) => entry.averageEffectiveOwnership,
      'asc'
    ),
    'Data FC': rankBy(teams, (entry) => entry.points.predicted, 'desc'),
    'Lucky FC': rankBy(
      teams,
      (entry) =>
        differenceOrNull(entry.points.gameweek, entry.points.predicted),
      'desc'
    ),
    'The Donkey': rankBy(teams, (entry) => entry.points.gameweek, 'asc'),
    'In Form FC': rankBy(
      teams,
      (entry) => entry.points.lastFiveGameweeks,
      'desc'
    ),
    'The Redknapp': rankBy(teams, (entry) => entry.transfers.netPoints, 'desc'),
    'Sack The Manager': rankBy(
      teams,
      (entry) => entry.transfers.netPoints,
      'asc'
    )
  };
};

const MAJOR_MINI_LEAGUE_INDEX = 317;
const SKY_SPORTS_MINI_LEAGUE_INDEX = 827316;
const UNWANTED_MINILEAGUES = [SKY_SPORTS_MINI_LEAGUE_INDEX];

export const getFilteredMiniLeagues = (
  miniLeagues: MiniLeague[] | undefined
): MiniLeague[] => {
  if (!miniLeagues) return [];
  return miniLeagues
    .filter((miniLeague) => miniLeague.id > MAJOR_MINI_LEAGUE_INDEX)
    .filter((miniLeague) => !UNWANTED_MINILEAGUES.includes(miniLeague.id));
};

/**
 * Chooses the entries the mini-league table shows. A user whose own page the
 * payload carries after the top of the table sees first place pinned above
 * that page, with the rest of the top hidden; everyone else sees the whole
 * payload.
 *
 * The split point is `userPageStartIndex`, the backend's own statement of
 * where the user's page begins. Nothing else can find it: ranks drift across
 * the boundary mid-gameweek, a user just past the top segment sits on a page
 * that abuts it so the two runs read as one, and the top segment is not a
 * fixed size across sources. A non-null `userLeagueRank` is not a promise
 * that there is a second run.
 */
export const selectMiniLeagueTableEntries = (
  league: LeagueDetail | null | undefined
): LeagueEntry[] => {
  const entries = league?.entries ?? [];
  const userPageStartIndex = league?.userPageStartIndex;
  if (
    userPageStartIndex === null ||
    userPageStartIndex === undefined ||
    userPageStartIndex <= 0 ||
    userPageStartIndex >= entries.length
  ) {
    return entries;
  }

  const [firstPlaceEntry] = entries;
  return [firstPlaceEntry, ...entries.slice(userPageStartIndex)];
};
